import { useMemo } from 'react';
import { Box, Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import type { Category, Product } from '@eprintsmart/shared';
import type { CategoryNode } from '../services/catalog';
import { CategoryProductRows } from '../components/CategoryProductRows';

/**
 * One row of the category tree, plus everything nested beneath it.
 *
 * Design notes, because the previous version was hard to read at 302
 * categories:
 *
 * - Depth is carried by a GUIDE RAIL (a left border on the nested block), not
 *   by indentation alone. Three levels of bare padding gave no way to trace a
 *   child back to its parent once the parent scrolled off.
 * - Type size steps down per level, so the level is legible without counting
 *   indents.
 * - Counts roll UP: a top-level group shows every product beneath it, which is
 *   the number you actually want there. The direct count is shown only where it
 *   differs, so the two can never be confused.
 * - Row actions are visible at rest and reserve their space. Revealing them on
 *   hover made every row except the one under the cursor look like it had no
 *   actions at all — on a list of 40 products, exactly one appeared to support
 *   uploading an image. Reserving the space also stops rows reflowing.
 */

export interface TreeStats {
  /** Products filed directly on this node (deepest-category membership). */
  direct: number;
  /** Products anywhere beneath it, including its own. */
  rollup: number;
  archived: number;
}

export function computeStats(
  node: CategoryNode,
  productsByCategory: Map<string, Product[]>,
  out: Map<string, TreeStats>,
): TreeStats {
  const own = productsByCategory.get(node.id) ?? [];
  let rollup = own.length;
  let archived = own.filter((p) => p.status === 'ARCHIVED').length;
  for (const child of node.children) {
    const s = computeStats(child, productsByCategory, out);
    rollup += s.rollup;
    archived += s.archived;
  }
  const stats = { direct: own.length, rollup, archived };
  out.set(node.id, stats);
  return stats;
}

/** Levels get progressively quieter so the hierarchy reads at a glance. */
const LEVEL_STYLE = [
  { size: '1rem', weight: 700, color: 'text.primary' },
  { size: '0.9rem', weight: 600, color: 'text.primary' },
  { size: '0.85rem', weight: 400, color: 'text.secondary' },
] as const;

export function TreeRow({
  node,
  depth,
  expanded,
  stats,
  matches,
  onToggle,
  onEdit,
  onDelete,
  productsByCategory,
  onImages,
  onArchive,
  onRestore,
}: {
  node: CategoryNode;
  depth: number;
  expanded: Set<string>;
  stats: Map<string, TreeStats>;
  /** null when no search is active; otherwise the ids that survived it. */
  matches: Set<string> | null;
  onToggle: (id: string) => void;
  onEdit: (draft: Partial<Category>) => void;
  onDelete: (node: CategoryNode) => void;
  productsByCategory: Map<string, Product[]>;
  onImages: (product: Product) => void;
  onArchive: (product: Product) => void;
  onRestore: (product: Product) => void;
}) {
  if (matches && !matches.has(node.id)) return null;

  const isOpen = expanded.has(node.id);
  const products = productsByCategory.get(node.id) ?? [];
  const s = stats.get(node.id) ?? { direct: 0, rollup: 0, archived: 0 };
  const hasChildren = node.children.length > 0 || products.length > 0;
  const style = LEVEL_STYLE[Math.min(depth, 2)];

  // A child repeating its parent's name reads as a duplicate in the tree
  // ("Self-Inking Stamps > Self-Inking Stamps") and is almost always an
  // accidental extra level rather than a real one.
  const repeatsParent =
    node.parentName != null &&
    node.parentName.trim().toLowerCase() === node.name.trim().toLowerCase();

  // An ACTIVE category with nothing beneath it renders as an empty page on the
  // storefront — a dead end for the customer, and invisible from here without
  // expanding every node.
  const emptyLive = node.status === 'ACTIVE' && s.rollup === 0;

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        onClick={() => hasChildren && onToggle(node.id)}
        sx={{
          py: depth === 0 ? 0.85 : 0.6,
          pr: 1,
          borderRadius: 1.5,
          cursor: hasChildren ? 'pointer' : 'default',
          bgcolor: depth === 0 ? 'action.hover' : 'transparent',
          '&:hover': { bgcolor: 'action.selected' },
          '&:hover .row-actions': { opacity: 1 },
        }}
      >
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onToggle(node.id);
          }}
          sx={{ visibility: hasChildren ? 'visible' : 'hidden' }}
        >
          {isOpen ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
        </IconButton>

        <Typography
          sx={{ fontSize: style.size, fontWeight: style.weight, color: style.color }}
          noWrap
        >
          {node.name}
        </Typography>

        {/* The slug is what the storefront URL uses, so it earns a place — but
            monospaced and dim, as reference rather than content. */}
        <Typography
          variant="caption"
          color="text.disabled"
          sx={{ fontFamily: 'monospace', display: { xs: 'none', md: 'block' } }}
          noWrap
        >
          /{node.slug}
        </Typography>

        {node.status !== 'ACTIVE' && (
          <Chip size="small" label={node.status} variant="outlined" sx={{ height: 20, fontSize: 11 }} />
        )}

        {repeatsParent && (
          <Tooltip title={`Same name as its parent category — "${node.parentName}" contains "${node.name}". Probably an extra level that can be merged.`}>
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              icon={<WarningAmberIcon sx={{ fontSize: 14 }} />}
              label="repeats parent"
              sx={{ height: 20, fontSize: 11 }}
            />
          </Tooltip>
        )}

        {emptyLive && (
          <Tooltip title="Live on the storefront but has no products — customers reach an empty page.">
            <Chip size="small" color="warning" variant="outlined" label="empty" sx={{ height: 20, fontSize: 11 }} />
          </Tooltip>
        )}

        <Box sx={{ flexGrow: 1 }} />

        {s.rollup > 0 && (
          <Tooltip
            title={
              s.direct === s.rollup
                ? `${s.rollup} product${s.rollup === 1 ? '' : 's'}`
                : `${s.rollup} in total beneath this, ${s.direct} filed directly here`
            }
          >
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              {s.rollup}
              {s.direct !== s.rollup && s.direct > 0 && ` (${s.direct} here)`}
              {s.archived > 0 && ` · ${s.archived} archived`}
            </Typography>
          </Tooltip>
        )}

        {/* Visible at rest, brighter on hover. Reserving the space stops rows
            reflowing, but opacity 0 still made the actions undiscoverable --
            you had to already know they were there to find them. */}
        <Stack
          direction="row"
          spacing={0.25}
          className="row-actions"
          onClick={(e) => e.stopPropagation()}
          sx={{ opacity: 0.55, transition: 'opacity 120ms', flexShrink: 0 }}
        >
          {node.level < 2 && (
            <Tooltip title="Add sub-category">
              <IconButton
                size="small"
                onClick={() =>
                  onEdit({
                    parentId: node.id,
                    level: (node.level + 1) as 0 | 1 | 2,
                    status: 'ACTIVE',
                    sortOrder: node.children.length,
                  })
                }
              >
                <AddIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Edit category">
            <IconButton size="small" onClick={() => onEdit(node)}>
              <EditOutlinedIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete category">
            <IconButton size="small" onClick={() => onDelete(node)}>
              <DeleteOutlineIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {isOpen && (
        <Box
          sx={{
            ml: 2.25,
            pl: 1.25,
            // The guide rail. Three levels of plain indentation were impossible
            // to trace once the parent scrolled out of view.
            borderLeft: '1px solid',
            borderColor: 'divider',
          }}
        >
          <CategoryProductRows
            products={products}
            depth={0}
            onImages={onImages}
            onArchive={onArchive}
            onRestore={onRestore}
          />
          {node.children.map((child) => (
            <TreeRow
              key={child.id}
              node={child}
              depth={depth + 1}
              expanded={expanded}
              stats={stats}
              matches={matches}
              onToggle={onToggle}
              onEdit={onEdit}
              onDelete={onDelete}
              productsByCategory={productsByCategory}
              onImages={onImages}
              onArchive={onArchive}
              onRestore={onRestore}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}

/**
 * Ids to render for a search term: every node whose own name, slug or product
 * names match, plus all their ancestors, so a deep match stays reachable.
 */
export function useSearchMatches(
  tree: CategoryNode[],
  productsByCategory: Map<string, Product[]>,
  term: string,
): { matches: Set<string> | null; autoExpand: Set<string> } {
  return useMemo(() => {
    const q = term.trim().toLowerCase();
    if (!q) return { matches: null, autoExpand: new Set<string>() };

    const matches = new Set<string>();
    const autoExpand = new Set<string>();

    const walk = (node: CategoryNode, ancestors: string[]): boolean => {
      const own =
        node.name.toLowerCase().includes(q) ||
        (node.slug ?? '').toLowerCase().includes(q) ||
        (productsByCategory.get(node.id) ?? []).some((p) => p.name.toLowerCase().includes(q));

      let hit = own;
      for (const child of node.children) {
        if (walk(child, [...ancestors, node.id])) hit = true;
      }
      if (hit) {
        matches.add(node.id);
        // Ancestors must render, and must be open, or the match is buried.
        for (const a of ancestors) {
          matches.add(a);
          autoExpand.add(a);
        }
        if (node.children.length > 0 || own) autoExpand.add(node.id);
      }
      return hit;
    };

    tree.forEach((n) => walk(n, []));
    return { matches, autoExpand };
  }, [tree, productsByCategory, term]);
}
